import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Bot, Send, Clock, Mic, MicOff, BarChart3, Brain, Trash2 } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const ROLE_ICONS = {
  student: "🎓", chef: "👨‍🍳", farmer: "🌾", teacher: "📚", "business consultant": "💼",
  doctor: "🩺", "fitness coach": "💪", "software developer": "💻", "financial advisor": "💰",
  "marketing expert": "📈", lawyer: "⚖️", "travel guide": "✈️", "content creator": "🎬",
  customer: "🛍️", "cloud kitchen": "🥘", vendor: "🏪", mentor: "🧑‍🏫", recruiter: "🔎",
  "corporate hr": "🏢", "cafe owner": "☕", "delivery rider": "🛵", "school admin": "🏫", nutritionist: "🥗",
};

// Visual role picker — mirrors the onboarding role grid
const ROLE_CARDS = [
  { key: "student", emoji: "🎓", label: "Student", tag: "Learn, earn & get hired" },
  { key: "customer", emoji: "🛍️", label: "Customer", tag: "Discover everything" },
  { key: "chef", emoji: "👨‍🍳", label: "Student Chef", tag: "Sell your food on campus" },
  { key: "cloud kitchen", emoji: "🥘", label: "Cloud Kitchen", tag: "Run a delivery kitchen" },
  { key: "farmer", emoji: "🌾", label: "Farmer", tag: "Sell directly to buyers" },
  { key: "vendor", emoji: "🏪", label: "Vendor", tag: "Grow your business" },
  { key: "content creator", emoji: "🎤", label: "Creator", tag: "Earn from your content" },
  { key: "mentor", emoji: "🧑‍🏫", label: "Mentor", tag: "Teach & guide students" },
  { key: "recruiter", emoji: "🔎", label: "Recruiter", tag: "Find & place candidates" },
  { key: "corporate hr", emoji: "🏢", label: "Corporate HR", tag: "Hire campus talent" },
  { key: "travel guide", emoji: "✈️", label: "Travel Partner", tag: "Sell trips & packages" },
  { key: "cafe owner", emoji: "☕", label: "Cafe Owner", tag: "Build your community" },
  { key: "delivery rider", emoji: "🛵", label: "Delivery Rider", tag: "Deliver & earn" },
  { key: "school admin", emoji: "🏫", label: "School Admin", tag: "Run your institution" },
  { key: "doctor", emoji: "🩺", label: "Doctor", tag: "Nutrition consults & diet plans" },
  { key: "nutritionist", emoji: "🥗", label: "Nutritionist", tag: "Custom diet programs" },
];

const RolePicker = ({ activeRole, onPick, onClose }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#09090B]/90 p-4 backdrop-blur-xl" data-testid="role-picker" onClick={onClose}>
    <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/10 bg-[#0A0A0F] p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-display text-xl font-extrabold">Switch role</p>
          <p className="mt-1 text-xs text-slate-500">FUNZI adapts instantly — pick who you are right now</p>
        </div>
        <button data-testid="role-picker-close" onClick={onClose} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-400 hover:text-white">✕</button>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {ROLE_CARDS.map((r) => (
          <button
            key={r.key}
            data-testid={`role-pick-${r.key.replace(/ /g, "-")}`}
            onClick={() => onPick(r)}
            className={`group rounded-2xl border p-4 text-left transition-all hover:-translate-y-1 ${
              activeRole === r.key
                ? "border-[#00D4FF]/60 bg-[#00D4FF]/10 shadow-[0_0_20px_rgba(0,212,255,0.25)]"
                : "border-white/10 bg-white/[0.03] hover:border-[#7B61FF]/50"
            }`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-xl transition-transform group-hover:scale-110">{r.emoji}</span>
            <p className="mt-3 text-sm font-bold">{r.label}</p>
            <p className="mt-0.5 text-[11px] text-slate-500">{r.tag}</p>
            {activeRole === r.key && <p className="mt-2 font-jbmono text-[9px] font-bold uppercase tracking-widest text-[#00FFD1]">Active</p>}
          </button>
        ))}
      </div>
    </div>
  </div>
);

export const AIAssistant = () => {
  const [state, setState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [dash, setDash] = useState(null);
  const [listening, setListening] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [memory, setMemory] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const voiceRef = useRef(false);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);

  const loadState = () => {
    axios.get(`${API}/assistant/state`, headers()).then((r) => setState(r.data)).catch(() => {});
    axios.get(`${API}/assistant/dashboard`, headers()).then((r) => setDash(r.data)).catch(() => {});
    axios.get(`${API}/assistant/worklog/analytics?days=30`, headers()).then((r) => setAnalytics(r.data)).catch(() => {});
    axios.get(`${API}/assistant/memory`, headers()).then((r) => setMemory(r.data.memory)).catch(() => {});
    axios.get(`${API}/assistant/quiz`, headers()).then((r) => { if (!r.data.done) setQuiz(r.data); }).catch(() => {});
  };
  useEffect(loadState, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (override) => {
    const msg = (typeof override === "string" ? override : input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { from: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/assistant/chat`, { message: msg }, headers());
      setMessages((m) => [...m, { from: "ai", text: data.reply, role: data.role }]);
      if (voiceRef.current) {
        voiceRef.current = false;
        speak(data.reply); // voice in → voice out, hands-free
      }
      if (data.switched) {
        setState((s) => ({ ...s, active_role: data.role }));
        loadState();
        window.dispatchEvent(new CustomEvent("funz:role-changed", { detail: data.role }));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setMessages((m) => [...m, { from: "ai", text: "Something went wrong — try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const switchTo = async (roleCard) => {
    setPickerOpen(false);
    send(`Switch to ${roleCard.key === "chef" ? "Chef" : roleCard.label}`);
  };

  const startVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input isn't supported in this browser.");
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      voiceRef.current = true;
      setInput(text);
      send(text); // hands-free — voice in, voice out
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const speak = (text) => {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1;
    window.speechSynthesis.speak(u);
  };

  const answerQuiz = async (optIdx) => {
    const next = [...quizAnswers, optIdx];
    if (quizStep < quiz.questions.length - 1) {
      setQuizAnswers(next);
      setQuizStep(quizStep + 1);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/assistant/quiz`, { answers: next }, headers());
      setQuiz(null);
      setMessages((m) => [...m, { from: "ai", text: `Based on your answers, I've set you up in ${ROLE_CARDS.find((c) => c.key === data.role)?.label || data.role} mode. ${data.greeting}`, role: data.role }]);
      setState((s) => ({ ...s, active_role: data.role }));
      loadState();
      window.dispatchEvent(new CustomEvent("funz:role-changed", { detail: data.role }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!state) return <LoaderCircle className="mx-auto mt-10 h-6 w-6 animate-spin text-[#00D4FF]" />;
  const role = state.role || {};

  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl" data-testid="ai-assistant">
      <div className="border-b border-white/10 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00D4FF] to-[#7B61FF] text-xl">
              {ROLE_ICONS[state.active_role] || "🤖"}
            </div>
            <div>
              <p className="font-display font-bold">FUNZI — {role.name || "Assistant"}</p>
              <p className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-1.5 w-1.5 rounded-full bg-[#00FFD1]" /> Active · type "Switch to Chef" to change role</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="assistant-switch-role" onClick={() => setPickerOpen(true)} className="rounded-full bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] px-4 py-2 font-jbmono text-[10px] font-bold uppercase tracking-wider text-white">
              Switch role
            </button>
            <button data-testid="assistant-analytics" onClick={() => document.getElementById("work-analytics")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-white" title="Work analytics">
              <BarChart3 className="h-4 w-4" />
            </button>
            <button data-testid="assistant-memory" onClick={() => document.getElementById("assistant-memory-panel")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-white" title="Memory">
              <Brain className="h-4 w-4" />
            </button>
          </div>
        </div>
        {dash && (
          <div className="mt-3 flex flex-wrap gap-3 rounded-2xl border border-white/10 bg-[#09090B]/60 px-4 py-2.5">
            <span className="flex items-center gap-1.5 font-jbmono text-[10px] text-slate-400"><Clock className="h-3 w-3" /> {dash.today_minutes}m today</span>
            <span className="font-jbmono text-[10px] text-slate-500">·</span>
            {Object.entries(dash.stats || {}).slice(0, 3).map(([k, v]) => (
              <span key={k} className="rounded-full bg-white/5 px-2.5 py-0.5 font-jbmono text-[9px] text-slate-400">{k}: {v}</span>
            ))}
          </div>
        )}
      </div>

      <div className="max-h-96 space-y-3 overflow-y-auto p-5" data-testid="assistant-messages">
        {messages.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/15 p-6 text-center">
            <p className="text-sm text-slate-400">Hi! I'm FUNZI. I'm currently in <span className="font-bold text-white">{role.name}</span> mode.</p>
            <p className="mt-2 font-jbmono text-[10px] uppercase tracking-widest text-slate-600">Try: "Switch to Chef" · "Switch to Student" · "Switch to Business Consultant"</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
              m.from === "user"
                ? "rounded-tr-sm bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] text-white"
                : "rounded-tl-sm border border-white/10 bg-white/5 text-slate-300"
            }`}>
              {m.from === "ai" && m.role && <p className="mb-1 font-jbmono text-[9px] uppercase tracking-widest text-[#00D4FF]">{ROLE_ICONS[m.role]} {m.role}</p>}
              {m.text}
              {m.from === "ai" && (
                <button onClick={() => speak(m.text)} className="ml-2 text-slate-500 hover:text-white" title="Speak">
                  <Bot className="inline h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><LoaderCircle className="h-4 w-4 animate-spin text-[#00D4FF]" /></div>}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-white/10 p-4">
        <div className="flex gap-2">
          <button data-testid="assistant-mic" onClick={listening ? () => recognitionRef.current?.stop() : startVoice}
            className={`rounded-full p-3 ${listening ? "bg-[#EF4444]/20 text-[#EF4444] animate-pulse" : "border border-white/10 text-slate-400 hover:text-white"}`}>
            {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
          <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={`Ask ${role.name || "FUNZI"}… or "Switch to [Role]"`}
            className="flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#00D4FF]/60" />
          <button data-testid="assistant-send" onClick={send} disabled={busy || !input.trim()}
            className="btn-primary !px-4 !py-3 disabled:opacity-50">
            <Send className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {ROLE_CARDS.slice(0, 6).map((r) => (
            <button key={r.key} data-testid={`role-${r.key.replace(/ /g, "-")}`} onClick={() => switchTo(r)}
              className={`rounded-full px-3 py-1.5 font-jbmono text-[9px] font-bold uppercase tracking-wider ${state.active_role === r.key ? "bg-[#00D4FF]/20 text-[#00D4FF] border border-[#00D4FF]/40" : "border border-white/10 text-slate-500 hover:text-white"}`}>
              {r.emoji} {r.label}
            </button>
          ))}
          <button data-testid="role-more" onClick={() => setPickerOpen(true)}
            className="rounded-full border border-dashed border-[#00D4FF]/40 px-3 py-1.5 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#00D4FF]">
            +10 more
          </button>
        </div>
      </div>
      {pickerOpen && <RolePicker activeRole={state.active_role} onPick={switchTo} onClose={() => setPickerOpen(false)} />}
      {quiz && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#09090B]/90 p-4 backdrop-blur-xl" data-testid="role-quiz">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0F] p-6">
            <p className="font-jbmono text-[10px] uppercase tracking-widest text-[#00FFD1]">Quick setup · {quizStep + 1}/3</p>
            <p className="mt-2 font-display text-xl font-extrabold" data-testid="quiz-question">{quiz.questions[quizStep].q}</p>
            <div className="mt-4 space-y-2">
              {quiz.questions[quizStep].options.map((opt, i) => (
                <button key={opt} data-testid={`quiz-opt-${i}`} onClick={() => answerQuiz(i)}
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-left text-sm transition-all hover:border-[#00D4FF]/50 hover:bg-[#00D4FF]/5">
                  {opt}
                </button>
              ))}
            </div>
            <button data-testid="quiz-skip" onClick={() => setQuiz(null)} className="mt-4 w-full text-center font-jbmono text-[10px] uppercase tracking-widest text-slate-600 hover:text-white">
              Skip for now
            </button>
          </div>
        </div>
      )}

      {analytics && (
        <div id="work-analytics" className="border-t border-white/10 p-5" data-testid="work-analytics">
          <p className="font-display text-sm font-bold">Work Analytics (30 days)</p>
          <p className="mt-1 text-xs text-slate-500">{analytics.insight}</p>
          <div className="mt-3 flex h-24 items-end gap-1.5">
            {analytics.by_day.slice(-14).map((d) => (
              <div key={d.date} className="flex-1 rounded-t-md bg-gradient-to-t from-[#7B61FF] to-[#00D4FF]" style={{ height: `${Math.min(100, (d.minutes / (analytics.avg_daily_minutes * 2 || 60)) * 100)}%` }} title={`${d.date}: ${d.minutes}m`} />
            ))}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="rounded-xl border border-white/10 bg-[#09090B]/80 p-3 text-center">
              <p className="font-display text-lg font-bold">{analytics.total_minutes}m</p>
              <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">Total</p>
            </div>
            <div className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/[0.06] p-3 text-center">
              <p className="font-display text-lg font-bold" data-testid="work-streak">{analytics.streak_days} {analytics.streak_days >= 3 ? "🔥" : "day"}{analytics.streak_days !== 1 && analytics.streak_days < 3 ? "s" : ""}</p>
              <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">Streak</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-[#09090B]/80 p-3 text-center">
              <p className="font-display text-lg font-bold">{analytics.week_minutes}<span className="text-xs text-slate-500">/{analytics.week_goal}m</span></p>
              <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">Week goal</p>
            </div>
          </div>
        </div>
      )}

      {memory.length > 0 && (
        <div id="assistant-memory-panel" className="border-t border-white/10 p-5" data-testid="assistant-memory-panel">
          <div className="flex items-center justify-between">
            <p className="font-display text-sm font-bold">FUNZI remembers ({role.name})</p>
            <button onClick={async () => { await axios.delete(`${API}/assistant/memory/${state.active_role}`, headers()); setMemory([]); toast.success("Memory cleared"); }} className="text-slate-500 hover:text-[#EF4444]" title="Clear memory">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="mt-2 space-y-1.5">
            {memory.map((m, i) => (
              <p key={i} className="rounded-xl bg-white/5 px-3 py-2 text-xs text-slate-300">{m.text}</p>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

